import React, { useCallback, useContext } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { AI_AUDIO_BRIEFING } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';

import { useAudioBriefing } from '../../../hooks/useAudioBriefing';
import { getSharedAttributes } from '../../../utils/getSharedAttributes';
import {
	getAudioLengthMs,
	getAudioCurrentTimeMs,
	getAudioProgress,
} from '../../../utils/audioTimeUtils';
import { AI_AUDIO_BRIEFING_INTERACTION_METRIC } from '../perf.config';

const i18n = defineMessages({
	buttonText: {
		id: 'audio.audio-briefing-popup.restart-button',
		defaultMessage: 'Play',
		description: 'Text in a button that will regenerate and play an audio briefing when clicked',
	},
});

export type RestartButtonProps = {
	audioRef: React.RefObject<HTMLAudioElement>;
	isLoading: boolean;
	hasDiverged: boolean;
	source: 'customizePanel' | 'sourcesPanel';
};

export type SourcesPanelRestartButtonProps = {
	audioRef: React.RefObject<HTMLAudioElement>;
	isLoading: boolean;
};

export type CustomizePanelRestartButtonProps = {
	audioRef: React.RefObject<HTMLAudioElement>;
	isLoading: boolean;
};

const RestartButton = ({ audioRef, isLoading, hasDiverged, source }: RestartButtonProps) => {
	const [
		{
			streamEnded,
			activeContent,
			customization,
			additionalResources,
			playbackSpeed,
			hasFetched,
			lastGeneratedBriefingConfig,
			hasEndBeenReached,
			activeFeature,
		},
		{ fetchBriefing },
	] = useAudioBriefing();

	const { locale } = useIntl();

	const { trackAIInteractionInit, trackAIResultAction } = useAIEventsInstrumentation();

	const experienceTracker = useContext(ExperienceTrackerContext);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const sendAnalyticsUIEvent = useCallback(
		(actionSubjectId: string, additionalAttributes?: Record<string, number | string>) => {
			const attributes = {
				audioLocale: locale,
				length: customization.length,
				tone: customization.tone,
				sourceCount: additionalResources.length,
				hasFetched,
				...additionalAttributes,
			};
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source,
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId,
					attributes,
				},
			}).fire();
		},
		[createAnalyticsEvent, locale, hasFetched, customization, additionalResources, source],
	);

	const stopExistingAudio = () => {
		if (audioRef?.current) {
			audioRef.current.pause();
		}

		const abortReason =
			source === 'customizePanel'
				? 'regenerated briefing from the customize panel'
				: 'regenerated briefing from the sources panel';

		if (!streamEnded) {
			experienceTracker.abort({
				name: AI_AUDIO_BRIEFING,
				reason: abortReason,
			});
		}

		const audioClosedAttributes = {
			audioLengthMs: getAudioLengthMs(audioRef),
			audioCurrentTimeMs: getAudioCurrentTimeMs(audioRef),
			percentCompleted: getAudioProgress(audioRef),
			reason: abortReason,
		};

		trackAIResultAction('audioClosed', {
			attributes: {
				...getSharedAttributes({
					customization: lastGeneratedBriefingConfig?.customization,
					additionalResources: lastGeneratedBriefingConfig?.additionalResources,
					playbackSpeed,
					hasFetched,
					activeFeature,
				}),
				...audioClosedAttributes,
				hasEndBeenReached,
			},
		});
	};

	const startNewAudio = () => {
		experienceTracker.start({
			name: AI_AUDIO_BRIEFING,
			attributes: {
				contentId: activeContent?.id,
			},
		});

		AI_AUDIO_BRIEFING_INTERACTION_METRIC.start();
		trackAIInteractionInit({
			attributes: getSharedAttributes({
				customization,
				additionalResources,
				playbackSpeed,
				hasFetched,
				activeFeature,
			}),
		});

		sendAnalyticsUIEvent('audioRegenerate');
		fetchBriefing();
	};

	const handleClick = () => {
		stopExistingAudio();
		startNewAudio();
	};

	return (
		<Button
			testId="restart-briefing-button"
			shouldFitContainer
			appearance="primary"
			onClick={handleClick}
			isLoading={isLoading}
			isDisabled={!hasDiverged}
		>
			<FormattedMessage {...i18n.buttonText} />
		</Button>
	);
};

export const SourcesPanelRestartButton = ({
	audioRef,
	isLoading,
}: SourcesPanelRestartButtonProps) => {
	const [{ additionalResources, lastGeneratedBriefingConfig }] = useAudioBriefing();

	const sourcesAreIdentical =
		additionalResources.length === lastGeneratedBriefingConfig?.additionalResources?.length &&
		lastGeneratedBriefingConfig?.additionalResources?.every(
			(lastGeneratedResource, index) =>
				additionalResources?.[index].ari === lastGeneratedResource.ari,
		);

	return (
		<RestartButton
			audioRef={audioRef}
			isLoading={isLoading}
			hasDiverged={!sourcesAreIdentical}
			source="sourcesPanel"
		/>
	);
};

export const CustomizePanelRestartButton = ({
	audioRef,
	isLoading,
}: CustomizePanelRestartButtonProps) => {
	const [{ customization, lastGeneratedBriefingConfig }] = useAudioBriefing();

	const hasDiverged =
		customization.tone !== lastGeneratedBriefingConfig?.customization?.tone ||
		customization.length !== lastGeneratedBriefingConfig?.customization?.length;

	return (
		<RestartButton
			audioRef={audioRef}
			isLoading={isLoading}
			hasDiverged={hasDiverged}
			source="customizePanel"
		/>
	);
};
